import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UserIdentity } from '../api/services/lupp/user-identity/dto.user-identity';
import type { Quote } from '../api/services/lupp/quotes/dto.quotes';
import { DEFAULT_LOCALE, DEFAULT_LANGUAGE } from '../config/constants';

type Theme = 'dark' | 'light';

/**
 * Label language override for i18n
 * - null: Use native locale language (e.g., 'de' for 'de-de')
 * - string: Override to specific language code (e.g., 'en', 'fr', 'de')
 */
export type Language = string | null;

export type UploadedProspectFile = {
  fileId: string;
  fileName: string;
  mimeType: string;
};

type SelectedProspect = {
  prospectIdentifier: string;
  prospectName: string;
  prospectEmail?: string;
  uploadedFiles?: UploadedProspectFile[];
} | null;

type Store = {
  theme: Theme;
  locale: string;
  language: Language;
  isSidebarCollapsed: boolean;
  selectedProspect: SelectedProspect;
  selectedQuote: Quote | null;
  userIdentity: UserIdentity | null;
  toggleTheme: () => void;
  setLocale: (locale: string) => void;
  setLanguage: (language: Language) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setSelectedProspect: (prospect: SelectedProspect) => void;
  clearSelectedProspect: () => void;
  setSelectedQuote: (quote: Quote | null) => void;
  clearSelectedQuote: () => void;
  setUserIdentity: (identity: UserIdentity | null) => void;
  clearUserIdentity: () => void;
  addUploadedProspectFile: (file: UploadedProspectFile) => void;
};

export const useStore = create<Store>()(
  persist(
    (set) => ({
      theme: 'dark',
      locale: DEFAULT_LOCALE,
      language: DEFAULT_LANGUAGE,
      isSidebarCollapsed: false,
      selectedProspect: null,
      selectedQuote: null,
      userIdentity: null,
      toggleTheme: () =>
        set((state) => ({
          theme: state.theme === 'dark' ? 'light' : 'dark',
        })),
      setLocale: (locale: string) =>
        set(() => ({
          locale,
        })),
      setLanguage: (lang: Language) =>
        set(() => ({
          language: lang,
        })),
      setSidebarCollapsed: (collapsed: boolean) =>
        set(() => ({
          isSidebarCollapsed: collapsed,
        })),
      setSelectedProspect: (prospect: SelectedProspect) =>
        set(() => ({
          selectedProspect: prospect,
        })),
      clearSelectedProspect: () =>
        set(() => ({
          selectedProspect: null,
        })),
      setSelectedQuote: (quote: Quote | null) =>
        set(() => ({
          selectedQuote: quote,
        })),
      clearSelectedQuote: () =>
        set(() => ({
          selectedQuote: null,
        })),
      setUserIdentity: (identity: UserIdentity | null) =>
        set(() => ({
          userIdentity: identity,
        })),
      clearUserIdentity: () =>
        set(() => ({
          userIdentity: null,
        })),
      addUploadedProspectFile: (file: UploadedProspectFile) =>
        set((state) => ({
          selectedProspect: state.selectedProspect
            ? {
                ...state.selectedProspect,
                uploadedFiles: [...(state.selectedProspect.uploadedFiles || []), file],
              }
            : null,
        })),
    }),
    {
      name: 'theme-storage',
      partialize: (state) => ({
        theme: state.theme,
        locale: state.locale,
        language: state.language,
        isSidebarCollapsed: state.isSidebarCollapsed,
        userIdentity: state.userIdentity,
      }),
    }
  )
);

/**
 * Access store state outside of React components.
 * This is Zustand's vanilla JS API — NOT a React hook.
 * Safe to use in interceptors, utilities, and other non-React contexts.
 */
export const getStoreState = () => useStore.getState();
